import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import { formatMoney } from '@/utils/format';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import { EducationGradeSelect } from '@/pages/education/components/EducationLookupSelects';

type StructureLine = {
  id: string;
  fee_head_id: string;
  fee_head_code?: string;
  fee_head_name?: string;
  grade_level_id?: string | null;
  grade_name?: string | null;
  amount: number;
  frequency: string;
};

type StructureDetail = {
  structure: {
    id: string;
    code: string;
    name: string;
    session_id: string;
    session_name?: string;
    session_code?: string;
    effective_on?: string | null;
    is_active?: number | boolean;
  };
  lines: StructureLine[];
};

type FeeHead = { id: string; code: string; name: string; is_active?: number | boolean };

type StructureForm = {
  code: string;
  name: string;
  effective_on?: string | null;
  is_active?: boolean;
};

type LineForm = {
  fee_head_id: string;
  grade_level_id?: string | null;
  amount: number;
  frequency: string;
};

const frequencyOptions = (t: (k: string) => string) => [
  { value: 'monthly', label: t('frequencyMonthly') },
  { value: 'term', label: t('frequencyTerm') },
  { value: 'annual', label: t('frequencyAnnual') },
  { value: 'one_time', label: t('frequencyOneTime') },
];

export default function FeeStructureDetailPage() {
  const { t } = useTranslation('education');
  const { t: tc } = useTranslation('common');
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.fee.manage') || user?.permissions?.includes('education.admin')
  );

  const [editStructureOpen, setEditStructureOpen] = useState(false);
  const [addLineOpen, setAddLineOpen] = useState(false);
  const [editingLine, setEditingLine] = useState<StructureLine | null>(null);
  const [structureForm] = Form.useForm<StructureForm>();
  const [addLineForm] = Form.useForm<LineForm>();
  const [editLineForm] = Form.useForm<LineForm>();

  const query = useQuery({
    queryKey: ['education-fee-structure', id],
    enabled: Boolean(id),
    queryFn: () => api.get<StructureDetail>(`/education/fees/structures/${id}`),
  });
  const heads = useQuery({
    queryKey: ['education-fee-heads'],
    queryFn: () => api.get<FeeHead[]>('/education/fees/heads'),
    enabled: canManage && (addLineOpen || Boolean(editingLine)),
  });

  const detail = query.data?.data;
  const structure = detail?.structure;
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['education-fee-structure', id] });
    queryClient.invalidateQueries({ queryKey: ['education-fee-structures'] });
  };

  const updateStructure = useMutation({
    mutationFn: (values: StructureForm) => api.put(`/education/fees/structures/${id}`, values),
    onSuccess: () => {
      message.success(t('feeStructureUpdated'));
      setEditStructureOpen(false);
      refresh();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const deleteStructure = useMutation({
    mutationFn: () => api.delete(`/education/fees/structures/${id}`),
    onSuccess: () => {
      message.success(t('feeStructureDeleted'));
      navigate('/education/fees/structures');
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const addLine = useMutation({
    mutationFn: (values: LineForm) =>
      api.post(`/education/fees/structures/${id}/lines`, {
        ...values,
        grade_level_id: values.grade_level_id || null,
      }),
    onSuccess: () => {
      message.success(t('feeStructureLineAdded'));
      setAddLineOpen(false);
      addLineForm.resetFields();
      refresh();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const updateLine = useMutation({
    mutationFn: (values: LineForm & { lineId: string }) =>
      api.put(`/education/fees/structures/${id}/lines/${values.lineId}`, {
        fee_head_id: values.fee_head_id,
        grade_level_id: values.grade_level_id || null,
        amount: values.amount,
        frequency: values.frequency,
      }),
    onSuccess: () => {
      message.success(t('feeStructureLineUpdated'));
      setEditingLine(null);
      refresh();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const deleteLine = useMutation({
    mutationFn: (lineId: string) => api.delete(`/education/fees/structures/${id}/lines/${lineId}`),
    onSuccess: () => {
      message.success(t('feeStructureLineDeleted'));
      refresh();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const activeHeads = (heads.data?.data ?? []).filter((h) => !(h.is_active === 0 || h.is_active === false));
  const headOptions = activeHeads.map((h) => ({
    value: h.id,
    label: `${h.code} — ${h.name}`,
  }));

  const openEditStructure = () => {
    if (!structure) return;
    structureForm.setFieldsValue({
      code: structure.code,
      name: structure.name,
      effective_on: structure.effective_on || undefined,
      is_active: !(structure.is_active === 0 || structure.is_active === false),
    });
    setEditStructureOpen(true);
  };

  const openEditLine = (row: StructureLine) => {
    setEditingLine(row);
    editLineForm.setFieldsValue({
      fee_head_id: row.fee_head_id,
      grade_level_id: row.grade_level_id || undefined,
      amount: Number(row.amount),
      frequency: row.frequency,
    });
  };

  return (
    <EducationPageChrome
      title={structure ? structure.name : t('feeStructures')}
      description={structure ? structure.code : t('feeStructuresIntro')}
      tags={
        structure ? (
          <Tag color={structure.is_active === 0 || structure.is_active === false ? undefined : 'success'}>
            {structure.is_active === 0 || structure.is_active === false ? t('inactive') : t('active')}
          </Tag>
        ) : undefined
      }
      extra={
        <Space wrap>
          {canManage && structure ? (
            <>
              <Button onClick={openEditStructure}>{t('editFeeStructure')}</Button>
              <Button
                type="primary"
                onClick={() => {
                  addLineForm.setFieldsValue({ frequency: 'term', amount: 0 });
                  setAddLineOpen(true);
                }}
              >
                {t('addFeeStructureLine')}
              </Button>
              <Popconfirm title={t('feeStructureDeleteConfirm')} onConfirm={() => deleteStructure.mutate()}>
                <Button danger loading={deleteStructure.isPending}>
                  {tc('delete')}
                </Button>
              </Popconfirm>
            </>
          ) : null}
          <Button onClick={() => navigate('/education/fees/structures')}>{t('backToFeeStructures')}</Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small" loading={query.isLoading}>
          {structure ? (
            <Space wrap size="large">
              <span>
                <strong>{t('code')}:</strong> {structure.code}
              </span>
              <span>
                <strong>{t('effectiveOn')}:</strong> {structure.effective_on || '—'}
              </span>
              <span>
                <strong>{t('session')}:</strong>{' '}
                {structure.session_name || structure.session_code || structure.session_id}
              </span>
            </Space>
          ) : null}
        </Card>

        <Card size="small" title={t('feeStructureLines')}>
          <Table
            rowKey="id"
            size="small"
            pagination={{ pageSize: 50 }}
            loading={query.isLoading}
            dataSource={detail?.lines ?? []}
            columns={[
              {
                title: t('feeHead'),
                render: (_: unknown, row: StructureLine) =>
                  `${row.fee_head_code || ''} — ${row.fee_head_name || ''}`.trim(),
              },
              {
                title: t('grade'),
                dataIndex: 'grade_name',
                render: (v: string | null | undefined) => v || t('allGrades'),
              },
              {
                title: t('amount'),
                dataIndex: 'amount',
                width: 120,
                render: (v: number) =>
                  formatMoney(Number(v), { currency: 'INR', display: 'plain', locale: 'en-IN' }),
              },
              { title: t('frequency'), dataIndex: 'frequency', width: 120 },
              ...(canManage
                ? [
                    {
                      title: t('actions'),
                      width: 180,
                      render: (_: unknown, row: StructureLine) => (
                        <Space>
                          <Button size="small" onClick={() => openEditLine(row)}>
                            {tc('edit')}
                          </Button>
                          <Popconfirm
                            title={t('feeStructureLineDeleteConfirm')}
                            onConfirm={() => deleteLine.mutate(row.id)}
                          >
                            <Button size="small" danger loading={deleteLine.isPending}>
                              {tc('delete')}
                            </Button>
                          </Popconfirm>
                        </Space>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        </Card>
      </Space>

      <Modal
        title={t('editFeeStructure')}
        open={editStructureOpen}
        onCancel={() => setEditStructureOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={structureForm} layout="vertical" onFinish={(values) => updateStructure.mutate(values)}>
          <Form.Item name="code" label={t('code')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="name" label={t('name')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="effective_on" label={t('effectiveOn')}>
            <Input placeholder="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item name="is_active" label={t('active')} valuePropName="checked">
            <Switch />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={updateStructure.isPending} block>
            {t('save')}
          </Button>
        </Form>
      </Modal>

      <Modal
        title={t('addFeeStructureLine')}
        open={addLineOpen}
        onCancel={() => setAddLineOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form
          form={addLineForm}
          layout="vertical"
          initialValues={{ frequency: 'term', amount: 0 }}
          onFinish={(values) => addLine.mutate(values)}
        >
          <Form.Item name="fee_head_id" label={t('feeHead')} rules={[{ required: true }]}>
            <Select options={headOptions} showSearch optionFilterProp="label" />
          </Form.Item>
          <Form.Item name="grade_level_id" label={t('grade')}>
            <EducationGradeSelect allowClear placeholder={t('allGrades')} />
          </Form.Item>
          <Form.Item name="amount" label={t('amount')} rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="frequency" label={t('frequency')} rules={[{ required: true }]}>
            <Select options={frequencyOptions(t)} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={addLine.isPending} block>
            {t('save')}
          </Button>
        </Form>
      </Modal>

      <Modal
        title={t('editFeeStructureLine')}
        open={Boolean(editingLine)}
        onCancel={() => setEditingLine(null)}
        footer={null}
        destroyOnClose
      >
        <Form
          form={editLineForm}
          layout="vertical"
          onFinish={(values) => editingLine && updateLine.mutate({ ...values, lineId: editingLine.id })}
        >
          <Form.Item name="fee_head_id" label={t('feeHead')} rules={[{ required: true }]}>
            <Select
              options={[
                ...headOptions,
                ...(editingLine && !headOptions.some((o) => o.value === editingLine.fee_head_id)
                  ? [
                      {
                        value: editingLine.fee_head_id,
                        label: `${editingLine.fee_head_code || ''} — ${editingLine.fee_head_name || ''}`,
                      },
                    ]
                  : []),
              ]}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item name="grade_level_id" label={t('grade')}>
            <EducationGradeSelect allowClear placeholder={t('allGrades')} />
          </Form.Item>
          <Form.Item name="amount" label={t('amount')} rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="frequency" label={t('frequency')} rules={[{ required: true }]}>
            <Select options={frequencyOptions(t)} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={updateLine.isPending} block>
            {t('save')}
          </Button>
        </Form>
      </Modal>
    </EducationPageChrome>
  );
}
